import r2wc from '@r2wc/react-to-web-component';
import FinderRecomnedations from './components/Recomnedations';
import ProductView from './components/ProductView';
import FinderSearch from './components/Search';
import Purchase from './components/Purchase';
import AlfinderToaster from './components/Toaster';
import SearchPageProducts from './components/SearchPageProducts';
import { getOrCreateSessionId } from './utils/get-or-create-session';
import {
  initializePosthog,
  setPersonPropertiesForFlags,
} from './utils/analytics';
// import { initializeSentry } from './utils/sentry';
import StartCheckout from './components/StartCheckout';
import ProductCard from './components/ProductCard';
import AlfinderModal from './components/Search/components/alfinder-modal';
import RecommendationsModal from './components/Recomnedations/components/recommendations-modal';
import FrequentlyBoughtTogether from './components/FrequentlyBoughtTogether';
import SizeGuide from './components/SizeGuide';
import withErrorBoundary from './components/ErrorBoundary/withErrorBoundary';
import BundleOffer from './components/BundleOffer';

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST;
// const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Get or create session ID first (needed for both PostHog and Sentry)
const sessionId = getOrCreateSessionId();

// Initialize Sentry first (for error tracking during initialization)
// if (SENTRY_DSN) {
//   try {
//     initializeSentry({
//       dsn: SENTRY_DSN,
//       environment: import.meta.env.VITE_DEPLOYMENT_NAMESPACE || 'development',
//       release: `alfinder-widget@${
//         import.meta.env.VITE_DEPLOYMENT_NAMESPACE || 'dev'
//       }-${Date.now()}`,
//       sessionId,
//     });
//   } catch (error) {
//     console.error('Failed to initialize Sentry:', error);
//   }
// }

// Initialize PostHog
if (POSTHOG_API_KEY && POSTHOG_HOST) {
  try {
    // Initialize PostHog with the session ID - this will now create an isolated instance
    initializePosthog(POSTHOG_API_KEY, POSTHOG_HOST, sessionId);
    setPersonPropertiesForFlags();
  } catch (error) {
    console.error('[Alfinder Widget] Failed to initialize analytics:', error);
  }
}

const WebComponentWidget = r2wc(
  withErrorBoundary(FinderSearch, 'FinderSearch'),
  {
    props: {
      store: 'string',
      provider: 'string',
      customer: 'string',
      preview: 'boolean',
      config: 'json',
      currency: 'string',
      issues: 'boolean',
      searchSettings: 'json',
      language: 'string',
    },
  },
);

const FinderWidget = r2wc(
  withErrorBoundary(FinderRecomnedations, 'FinderRecomnedations'),
  {
    props: {
      store: 'string',
      page: 'string',
      customer: 'string',
      preview: 'boolean',
      provider: 'string',
      product: 'string',
      config: 'json',
      currency: 'string',
      recommendationId: 'string',
      preloadedProducts: 'json',
      preloadedRecommendation: 'json',
      language: 'string',
    },
  },
);

const ProductViewWidget = r2wc(ProductView, {
  props: {
    customer: 'string',
    store: 'string',
    product: 'string',
    provider: 'string',
  },
});

const ProductPurchasedWidget = r2wc(withErrorBoundary(Purchase, 'Purchase'), {
  props: {
    store: 'string',
    provider: 'string',
    customer: 'string',
  },
});

const ToaserWidget = r2wc(AlfinderToaster);

const SearchPageProductsWidget = r2wc(
  withErrorBoundary(SearchPageProducts, 'SearchPageProducts'),
  {
    props: {
      customer: 'string',
      store: 'string',
      provider: 'string',
      color: 'string',
      currency: 'string',
      language: 'string',
      locale: 'string',
    },
  },
);

const StartCheckoutWidget = r2wc(
  withErrorBoundary(StartCheckout, 'StartCheckout'),
  {
    props: {
      store: 'string',
      products: 'string',
      provider: 'string',
      customer: 'string',
    },
  },
);

const ProductCardWidget = r2wc(withErrorBoundary(ProductCard, 'ProductCard'), {
  props: {
    product: 'json',
    color: 'string',
    currency: 'string',
    config: 'json',
    handleProductClick: 'function',
    toggleFavorites: 'function',
    handleAddToCart: 'function',
    loadingStates: 'json',
    preview: 'boolean',
    type: 'boolean',
    showDiscounts: 'boolean',
    language: 'string',
  },
});

const AlfinderModalWidget = r2wc(
  withErrorBoundary(AlfinderModal, 'AlfinderModal'),
  {
    props: {
      store: 'string',
      provider: 'string',
      customer: 'string',
      query: 'string',
      currency: 'string',
      open: 'boolean',
    },
  },
);

const RecommendationsModalWidget = r2wc(
  withErrorBoundary(RecommendationsModal, 'RecommendationsModal'),
  {
    props: {
      store: 'string',
      provider: 'string',
      customer: 'string',
      open: 'boolean',
      currency: 'string',
      locale: 'string',
      primaryColor: 'string',
      product: 'string',
      preloadedProducts: 'json',
      language: 'string',
      fptTitle: 'string',
      fptTitleEn: 'string',
    },
  },
);

const FrequentlyBoughtTogetherWidget = r2wc(
  withErrorBoundary(FrequentlyBoughtTogether, 'FrequentlyBoughtTogether'),
  {
    props: {
      currency: 'string',
      locale: 'string',
      primaryColor: 'string',
      store: 'string',
      provider: 'string',
      product: 'string',
      customer: 'string',
      hideTitle: 'boolean',
      skipInitialLoading: 'boolean',
      preloadedProducts: 'json',
      onModalClose: 'function',
      type: 'string',
      language: 'string',
      fptTitle: 'string',
      fptTitleEn: 'string',
    },
  },
);

const SizeGuideWidget = r2wc(withErrorBoundary(SizeGuide, 'SizeGuide'), {
  props: {
    open: 'boolean',
    primaryColor: 'string',
    locale: 'string',
    buttonText: 'string',
    modalTitle: 'string',
    sizeData: 'json',
    provider: 'string',
  },
});

const BundleOfferWidget = r2wc(withErrorBoundary(BundleOffer, 'BundleOffer'), {
  props: {
    currency: 'string',
    locale: 'string',
    primaryColor: 'string',
    store: 'string',
    provider: 'string',
    product: 'string',
    customer: 'string',
    skipInitialLoading: 'boolean',
    preloadedProducts: 'json',
    promotionTitle: 'json',
    promotionDescription: 'json',
    carouselOptions: 'json',
    language: 'string',
    fptTitle: 'string',
    fptTitleEn: 'string',
  },
});

customElements.define('alfinder-product-card', ProductCardWidget);
customElements.define('alfinder-search', WebComponentWidget);
customElements.define('alfinder-recommendations', FinderWidget);
customElements.define('alfinder-product-view', ProductViewWidget);
customElements.define('alfinder-product-purchased', ProductPurchasedWidget);
customElements.define('alfinder-toaster', ToaserWidget);
customElements.define(
  'alfinder-search-page-products',
  SearchPageProductsWidget,
);
customElements.define('alfinder-start-checkout', StartCheckoutWidget);
customElements.define('alfinder-modal', AlfinderModalWidget);
customElements.define(
  'alfinder-recommendations-modal',
  RecommendationsModalWidget,
);
customElements.define(
  'alfinder-frequently-bought-together',
  FrequentlyBoughtTogetherWidget,
);
customElements.define('alfinder-size-guide', SizeGuideWidget);
customElements.define('alfinder-bundle-offer', BundleOfferWidget);
